import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReusableTable from "components/Tables/ReusableTable";
import { Flex, Box, Text, Badge } from "@chakra-ui/react";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { EllipsisVertical, Wrench, Truck } from "lucide-react";
import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";
import ViewDelivery from "./components/ViewDeliveryDetails.js";
import ManageDelivery from "./components/ManageDelivery.js";
import getStatusColor from "./utils/StatusColor.js";
import ThreeDotMenu from "components/Menus/ThreeDotMenu.js";
import { fetchDeliveriesAction } from "../../../state/actions/deliveryActions";
import { formatDate } from "utils/dateFormatter";
import { fetchCommissionsAction } from "state/actions/driverCommissionsActions.js";
import { useCustomNotification } from "components/Toasts/OpenNotification.js";


const DeiliveryManagement = () => {
  const dispatch = useDispatch();
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const notify = useCustomNotification();
  const [isViewDeliveryDrawerOpen, setViewDeliveryDrawerOpen] = useState(false);
  const [isManageDeliveryDrawerOpen, setManageDeliveryDrawerOpen] = useState(false);
  const { deliveries, loading, error, message } = useSelector((state) => state.deliveries || {});


  useEffect(() => {
    dispatch(fetchDeliveriesAction());
    dispatch(fetchCommissionsAction());
  }, [dispatch]);

  const getDisplayName = (user) => {
    if (!user) return "N/A";
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return fullName || user.username || user.email || "N/A";
  };

  const tableData = useMemo(() => {
    if (!Array.isArray(deliveries?.data)) return [];

    return deliveries.data.map((del) => {
      const delivery = del.delivery || {};
      const sender = del.sender || {};
      const recipient = del.recipient || {};
      const driver = del.driver || {};
      const packageData = del.package || {};

      return {
        deliveryId: delivery.delivery_id,
        trackingNumber: delivery.tracking_number,
        sender: getDisplayName(sender),
        recipient: getDisplayName(recipient),
        driver: getDisplayName(driver),
        status: delivery.delivery_status,
        pickupTime: delivery.pickup_time ? formatDate(delivery.pickup_time) : "N/A",
        dropoffTime: delivery.dropoff_time ? formatDate(delivery.dropoff_time) : "N/A",
        createdAt: delivery.created_at ? formatDate(delivery.created_at) : "N/A",
        updatedAt: delivery.updated_at ? formatDate(delivery.updated_at) : "N/A",
        original: {
          delivery,
          sender,
          recipient,
          driver,
          package: packageData,
        },
      };
    });
  }, [deliveries]);




  const generateMenuItems = (row) => [
    {
      icon: <Truck size={16} />,
      text: 'View Delivery',
      onClick: () => handleViewDelivery(row.original),
    },
    {
      icon: <Wrench size={16} />,
      text: 'Manage Delivery',
      onClick: () => handleManageDelivery(row.original),
    },
  ];

  const handleViewDelivery = (del) => {
    setSelectedDelivery(del);
    setViewDeliveryDrawerOpen(true);
  };

  const handleManageDelivery = (del) => {
    setSelectedDelivery(del);
    setManageDeliveryDrawerOpen(true);
  };

  const columnsData = [
    {
      Header: "Tracking Number",
      accessor: "trackingNumber",
      width: '10%'
    },
    {
      Header: "Pickup Time",
      accessor: "pickupTime",
      width: '15%'
    },
    {
      Header: "Dropoff Time",
      accessor: "dropoffTime",
      width: '20%'
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge colorScheme={getStatusColor(value)} borderRadius="full" px={2}>
          {value}
        </Badge>
      ),
      width: '10%'
    },
    {
      Header: "Created",
      accessor: "createdAt",
      width: '10%'
    },
    {
      Header: "Updated",
      accessor: "updatedAt",
      width: '10%'
    },
    {
      Header: "Actions",
      accessor: "moreOptions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={generateMenuItems(row)}
        />
      ),
      width: '5%'
    },
  ];




  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>

      <Box p={6}>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          {loading ? (
            <Skeleton height="20px" width="140px" />
          ) : (
            <Text fontSize="md" fontWeight="bold">
              All Deliveries: {deliveries?.total ?? 0}
            </Text>

          )}
        </Flex>
      </Box>
      <ReusableTable
        columnsData={columnsData}
        tableData={tableData}
        initialState={{
          pageSize: 10,
          sortBy: [{ id: 'createdAt', desc: true }]
        }}
        isLoading={loading}
      />


      <ReusableDrawer
        isOpen={isViewDeliveryDrawerOpen}
        onClose={() => setViewDeliveryDrawerOpen(false)}
        headerContent={selectedDelivery && (
          <Text fontSize="lg" fontWeight="bold">
            View Delivery - {selectedDelivery.trackingNumber}
          </Text>
        )}
        bodyContent={selectedDelivery && (
          <ViewDelivery
            delivery={selectedDelivery}
            onClose={() => setViewDeliveryDrawerOpen(false)}
          />
        )}
        size="lg"
      />

      <ReusableDrawer
        isOpen={isManageDeliveryDrawerOpen}
        onClose={() => setManageDeliveryDrawerOpen(false)}
        headerContent={selectedDelivery && (
          <Text fontSize="lg" fontWeight="bold">
            Manage Delivery - #{selectedDelivery.trackingNumber}
          </Text>
        )}
        bodyContent={selectedDelivery && (
          <ManageDelivery
            delivery={selectedDelivery}
            onClose={() => setManageDeliveryDrawerOpen(false)}
          />
        )}
        size="lg"
      />

    </Flex>
  );
};

export default DeiliveryManagement;
